import { useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import {
  GET_EMAIL_THREAD_AROUND,
  GET_EMAIL_THREAD_PAGE,
} from '../../../lib/graphql/queries/organizations';
import type { GQLMessageItem } from './useContact';
import { applySessionChange, emptySession, type PagedSession } from './emailThreadSession';

export type EmailThreadMessage = GQLMessageItem;

interface Page {
  items: EmailThreadMessage[];
  total?: number;
  hasMore: boolean;
}

interface PageData {
  organization: { messages: Page } | null;
}

interface AroundData {
  organization: { anchor: Page; older: Page; newer: Page } | null;
}

const DEFAULT_LIMIT = 10;

/** Which emails a thread lists; null lists every kind. */
export type EmailThreadKinds = ('AGENTIC' | 'LEGACY')[] | null;

// Remi conversations by default: template sends (eAlerts, flow emails) have no replies to thread.
const DEFAULT_KINDS: EmailThreadKinds = ['AGENTIC'];

/**
 * An email thread in chronological order, paged in both directions.
 *
 * Opened at `anchorMessageId` it loads that message with its nearest older
 * and newer neighbours; otherwise it loads the latest messages. When the
 * caller only knows the message, the message is read first to find its
 * conversation. `loadOlder` / `loadNewer` page past the first and last
 * rendered messages using the backend's exclusive `before` / `after` cursors.
 */
export function useEmailThreadGQL({
  conversationId,
  anchorMessageId,
  enabled,
  limit = DEFAULT_LIMIT,
  emailKinds = DEFAULT_KINDS,
}: {
  conversationId?: string | null;
  anchorMessageId?: string | null;
  enabled: boolean;
  limit?: number;
  emailKinds?: EmailThreadKinds;
}) {
  const orgId = useOrganizationStore((s) => s.organization?.id) ?? '';
  const apolloClient = useApolloClient();

  // Message-only open: read the message to learn which thread it belongs to.
  const lookup = useQuery<PageData>(GET_EMAIL_THREAD_PAGE, {
    variables: {
      organizationId: orgId,
      messageId: anchorMessageId,
      sortOrder: 'DESC',
      limit: 1,
      emailKinds,
    },
    skip: !enabled || !orgId || !anchorMessageId || !!conversationId,
    fetchPolicy: 'cache-first',
  });
  const lookedUp = lookup.data?.organization?.messages?.items?.[0] ?? null;
  const threadId = conversationId || lookedUp?.conversationId || null;
  // A message with no conversation (a standalone send) is a thread of one.
  const standalone = !conversationId && !!lookup.data && !lookedUp?.conversationId;

  const around = useQuery<AroundData>(GET_EMAIL_THREAD_AROUND, {
    variables: {
      organizationId: orgId,
      conversationId: threadId,
      anchor: anchorMessageId,
      limit,
      emailKinds,
    },
    skip: !enabled || !orgId || !threadId || !anchorMessageId,
    fetchPolicy: 'cache-and-network',
  });

  const latest = useQuery<PageData>(GET_EMAIL_THREAD_PAGE, {
    variables: {
      organizationId: orgId,
      conversationId: threadId,
      sortOrder: 'DESC',
      limit,
      emailKinds,
    },
    skip: !enabled || !orgId || !threadId || !!anchorMessageId,
    fetchPolicy: 'cache-and-network',
  });

  // Each open of a thread is its own session: reopening, or switching organization,
  // thread or anchor, starts a new one. Pages loaded past the first ones belong to
  // the session that asked for them, so a page that resolves after its session ended
  // is dropped instead of landing in another thread.
  const [openCount, setOpenCount] = useState(enabled ? 1 : 0);
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (wasEnabled !== enabled) {
    setWasEnabled(enabled);
    if (enabled) setOpenCount((n) => n + 1);
  }
  const sessionKey = `${orgId}|${openCount}|${conversationId ?? ''}|${anchorMessageId ?? ''}`;
  const [paged, setPaged] = useState<PagedSession>(() => emptySession(sessionKey));
  const session = paged.key === sessionKey ? paged : emptySession(sessionKey);
  const { extraOlder, extraNewer, olderHasMore, newerHasMore } = session;
  const isLoadingOlder = session.loadingOlder;
  const isLoadingNewer = session.loadingNewer;

  let initial: EmailThreadMessage[] = [];
  let initialHasOlder = false;
  let initialHasNewer = false;
  let total = 0;
  if (anchorMessageId && around.data?.organization) {
    const { anchor, older, newer } = around.data.organization;
    initial = [...[...older.items].reverse(), ...anchor.items, ...newer.items];
    initialHasOlder = older.hasMore;
    initialHasNewer = newer.hasMore;
    total = older.total ?? initial.length;
  } else if (!anchorMessageId && latest.data?.organization) {
    const page = latest.data.organization.messages;
    initial = [...page.items].reverse();
    initialHasOlder = page.hasMore;
    total = page.total ?? initial.length;
  } else if (standalone && lookedUp) {
    initial = [lookedUp];
    total = 1;
  }

  // Cursors are strictly older/newer than the edge message, so pages never overlap.
  const messages = [...extraOlder, ...initial, ...extraNewer];
  const hasOlder = olderHasMore ?? initialHasOlder;
  const hasNewer = newerHasMore ?? initialHasNewer;

  const fetchPage = useCallback(
    async (cursor: { before?: string; after?: string }, sortOrder: 'ASC' | 'DESC') => {
      const result = await apolloClient.query<PageData>({
        query: GET_EMAIL_THREAD_PAGE,
        variables: {
          organizationId: orgId,
          conversationId: threadId,
          ...cursor,
          sortOrder,
          limit,
          emailKinds,
        },
        fetchPolicy: 'network-only',
      });
      return result.data?.organization?.messages ?? { items: [], hasMore: false };
    },
    [apolloClient, orgId, threadId, limit, emailKinds]
  );

  const oldestId = messages[0]?.messageId;
  const newestId = messages[messages.length - 1]?.messageId;

  // Applies a change to the requesting session only; a newer session's state is left alone.
  const updateSession = useCallback(
    (key: string, change: (current: PagedSession) => PagedSession, start = false) =>
      setPaged((prev) => applySessionChange(prev, key, change, start)),
    []
  );

  const loadOlder = useCallback(async () => {
    if (isLoadingOlder || !hasOlder || !threadId || !oldestId) return;
    const key = sessionKey;
    updateSession(key, (cur) => ({ ...cur, loadingOlder: true }), true);
    try {
      const page = await fetchPage({ before: oldestId }, 'DESC');
      updateSession(key, (cur) => ({
        ...cur,
        extraOlder: [...[...page.items].reverse(), ...cur.extraOlder],
        olderHasMore: page.hasMore,
      }));
    } finally {
      updateSession(key, (cur) => ({ ...cur, loadingOlder: false }));
    }
  }, [isLoadingOlder, hasOlder, threadId, oldestId, fetchPage, sessionKey, updateSession]);

  const loadNewer = useCallback(async () => {
    if (isLoadingNewer || !hasNewer || !threadId || !newestId) return;
    const key = sessionKey;
    updateSession(key, (cur) => ({ ...cur, loadingNewer: true }), true);
    try {
      const page = await fetchPage({ after: newestId }, 'ASC');
      updateSession(key, (cur) => ({
        ...cur,
        extraNewer: [...cur.extraNewer, ...page.items],
        newerHasMore: page.hasMore,
      }));
    } finally {
      updateSession(key, (cur) => ({ ...cur, loadingNewer: false }));
    }
  }, [isLoadingNewer, hasNewer, threadId, newestId, fetchPage, sessionKey, updateSession]);

  const loading = messages.length === 0 && (lookup.loading || around.loading || latest.loading);

  return {
    messages,
    total,
    conversationId: threadId,
    loading,
    error: lookup.error || around.error || latest.error,
    hasOlder,
    hasNewer,
    loadOlder,
    loadNewer,
    isLoadingOlder,
    isLoadingNewer,
  };
}
